import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { UserState } from '../../../../states/user.state';
import { IPersistentFilter, ITimesheetFilterSettings } from '../../../../interfaces/employee/IPersistentFilter';
import { UpdatePersistentFilters } from '../../../../states/user.actions';
import { PersistentFilterProperties } from '../../../../enums/PersistentFilterProperties.enum';
import { IPersistentFilterUpdateBody } from '../../../../interfaces/IPersistentFilterUpdateBody';
import { LoadingMode } from '../../../../enums/LoadingMode.enum';
import { NotificationService } from '../../../../services/notification.service';
import { INotification } from '../../../../interfaces/INotification';
import { UniversalDropdownComponent } from '../../../universal-dropdown/universal-dropdown.component';
import { LoadingComponent } from '../../../loading/loading.component';
import { FormControlPipe } from '../../../../pipes/formControl.pipe';

@Component({
  selector: 'app-timesheet-settings-dialog',
  templateUrl: './timesheet-settings-dialog.component.html',
  styleUrls: ['./timesheet-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    UniversalDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    LoadingComponent,
    MatDialogActions,
    AsyncPipe,
    TranslatePipe,
    FormControlPipe,
    MatCheckbox,
  ],
})
export class TimesheetSettingsDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<TimesheetSettingsDialogComponent>>(MatDialogRef);
  private store = inject(Store);
  private notificationService = inject(NotificationService);
  private userPersistentFilters$: Observable<IPersistentFilter> = this.store.select<IPersistentFilter>(UserState.filters);
  protected isLoading$: Observable<boolean> = this.store.select<boolean>(UserState.isUserPersistentFilterLoading);
  protected timesheetSettingsForm: FormGroup;
  protected timesheetPageOptions: number[] = [10, 25, 50, 100];
  protected showProjectNumberColumnInTimesheets = false;
  protected readonly LoadingMode = LoadingMode;
  private userScdGid: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    this.loadUserPersistentFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public save(): void {
    this.updatePersistentFilters(this.timesheetSettingsForm.value);
  }

  public reset(): void {
    this.updatePersistentFilters(null);
  }

  protected onClickNumberColumn(showNumberColumn: MatCheckboxChange) {
    this.showProjectNumberColumnInTimesheets = showNumberColumn.checked;
    this.timesheetSettingsForm.patchValue({
      showProjectNumberColumnInTimesheets: showNumberColumn.checked,
    });
  }

  private loadUserPersistentFilters(): void {
    this.userPersistentFilters$
      .pipe(
        tap((filters: IPersistentFilter) => this.initializeForm(filters)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private initializeForm(filters: IPersistentFilter): void {
    this.userScdGid = filters.scdGid;
    this.timesheetSettingsForm = new FormGroup({
      pageSize: new FormControl({
        value: filters.timesheetFilterSettings
          ? JSON.parse(filters.timesheetFilterSettings)?.pageSize
            ? JSON.parse(filters.timesheetFilterSettings).pageSize
            : 10
          : 10,
        disabled: false,
      }),
      showProjectNumberColumnInTimesheets: new FormControl({
        value: filters.timesheetFilterSettings
          ? JSON.parse(filters.timesheetFilterSettings)?.showProjectNumberColumnInTimesheets
            ? JSON.parse(filters.timesheetFilterSettings).showProjectNumberColumnInTimesheets
            : false
          : false,
        disabled: false,
      }),
    });
    this.showProjectNumberColumnInTimesheets = this.timesheetSettingsForm.get('showProjectNumberColumnInTimesheets').value;
  }

  private updatePersistentFilters(updatedPreSelectedPersistentFilters: ITimesheetFilterSettings): void {
    const persistentFilterBody: IPersistentFilterUpdateBody = {
      filterView: {
        timesheetFilterSettings: updatedPreSelectedPersistentFilters ? JSON.stringify(updatedPreSelectedPersistentFilters) : null,
        scdGid: this.userScdGid,
      },
      propertiesToUpdate: [PersistentFilterProperties.TIMESHEETS_FILTER],
    };
    this.store
      .dispatch(new UpdatePersistentFilters(persistentFilterBody, 'timesheetFilterSettings'))
      .pipe(
        tap(() => this.showConfirmationMessageAndReload()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private showConfirmationMessageAndReload(): void {
    const notification: INotification = {
      type: 'Success',
      title: 'header.timesheetSettings',
      desc: `header.settingsMenu.timesheetSettings.successMessage`,
    };
    this.notificationService.showMessage(notification);
    this.closeDialog();
  }
}
